// Shooting-feel effects layered on the FX lane's GPU systems (no new draws, no allocations per
// frame): per-weapon muzzle extras, sniper smoke trails, spent casings, directional impact
// spray, damaged robots that smoke and spit sparks, headshot pops, shatter bursts and wrecks
// that keep burning for a few seconds.

import * as THREE from 'three';
import type { Fx } from '../render/fx/index';
import { INKS } from '../render/fx/index';
import { PShape, PFlag } from '../render/fx/particles';
import { CMode, COrient, CFlag } from '../render/fx/cards';
import { linRGB } from '../render/fx/gpu';
import { PAL } from '../render/palette';
import { ENEMIES } from '../../sim/defs';
import { W } from '../../sim/weapons';
import { DeathKind } from '../render/contracts';
import { Shell } from './recoil';

const C = {
  white: linRGB(0xfff6e4), cream: linRGB(PAL.warmCream), sun: linRGB(PAL.sunCore), amber: linRGB(PAL.primeAmber),
  fireCore: linRGB(PAL.thrusterCore), fire: linRGB(PAL.thrusterRing), brass: linRGB(PAL.brass), brassHi: linRGB(PAL.brassTrim),
  ink: linRGB(PAL.inkDark), smokeLight: linRGB(0xc9bca2), smokeMid: linRGB(0x7a6a56), smokeDark: linRGB(0x4b3f33),
  soot: linRGB(0x2a231a), ember: linRGB(0xff7a2a), gunmetal: linRGB(PAL.gunmetal), chrome: linRGB(PAL.chrome),
  hullRed: linRGB(0xb23a24), violet: linRGB(0xb98cff), steel: linRGB(PAL.steel), blue: linRGB(PAL.shieldBlue),
};
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

const MAX_EM = 48;
/** emitter kinds: a neck spark fountain, a burning wreck, a smoking wreck */
const enum Em { Fountain = 0, Burn = 1, Smoke = 2 }

export class FeelFx {
  private mul: number;
  private time = 0;
  private dx = 0; private dy = 0; private dz = 0;
  // emitters: struct of arrays
  private eK = new Int8Array(MAX_EM).fill(-1);
  private eX = new Float32Array(MAX_EM); private eY = new Float32Array(MAX_EM); private eZ = new Float32Array(MAX_EM);
  private eEnd = new Float32Array(MAX_EM); private eAcc = new Float32Array(MAX_EM); private eS = new Float32Array(MAX_EM);
  private eN = 0;
  // damaged-robot smoke: per robot id, time of the next puff
  private nextPuff = new Map<number, number>();
  private v = new THREE.Vector3();

  constructor(private fx: Fx, quality: number) {
    this.mul = [0.45, 0.75, 1, 1.2][Math.max(0, Math.min(3, quality | 0))];
  }

  private n(k: number) { return Math.max(1, Math.round(k * this.mul)); }
  private dir(up = 0) {
    let x, y, z, l;
    do { x = Math.random() * 2 - 1; y = Math.random() * 2 - 1; z = Math.random() * 2 - 1; l = x * x + y * y + z * z; } while (l > 1 || l < 1e-4);
    l = Math.sqrt(l); x /= l; y /= l; z /= l;
    y = y * (1 - up) + up * Math.abs(y) + up * 0.5;
    l = Math.sqrt(x * x + y * y + z * z);
    this.dx = x / l; this.dy = y / l; this.dz = z / l;
  }
  private ground(x: number, y: number, z: number) {
    const w = (this.fx as unknown as { world?: { support?: (x: number, z: number, y: number) => number } }).world;
    const g = w && w.support ? w.support(x, z, y + 0.4) : y - 2;
    return g < -1e6 ? y - 60 : g;
  }

  // ---------------------------------------------------------------- muzzle extras
  /** layered on top of fx.muzzle: what makes each gun's report look like its own */
  muzzle(wid: number, x: number, y: number, z: number, dx: number, dy: number, dz: number, local: boolean) {
    const P = this.fx.particles, Cd = this.fx.cards, k = local ? 1 : 0.8;
    switch (wid) {
      case W.BoltSniper: {
        // a hard blast ring at the crown, side-vent jets from the brake, and a slow smoke cloud
        Cd.spawn(CMode.Shock, COrient.Plane, x + dx * 0.3, y + dy * 0.3, z + dz * 0.3, dx, dy, dz, 0.08, 0, 0.06, 0.34, C.cream, 0.8, C.sun, CFlag.Add, 0.12, 0, 0, 0, 2 * k);
        Cd.spawn(CMode.Flash, COrient.Billboard, x + dx * 0.4, y + dy * 0.4, z + dz * 0.4, 0, 0, 0, 0.07, 0, 0.5, 0.9, C.white, 1, C.amber, CFlag.Add, 8, Math.random(), 0, Math.random() * 6, 3 * k);
        const rx = -dz, rz = dx; // right of the barrel (flat)
        for (let s = -1; s <= 1; s += 2) {
          for (let i = 0; i < this.n(4); i++) {
            const sp = rnd(4, 8);
            P.spawn(x, y, z, rx * s * sp + dx * 1.5, rnd(-0.3, 0.6), rz * s * sp + dz * 1.5, rnd(0.6, 1.1), 0, -0.15, 3.5, -1e5,
              PShape.Puff, PFlag.Soft, 0.1, rnd(0.45, 0.7), rnd(-1, 1), C.smokeLight, 0.85, C.smokeLight, 0.5, 1, 0.95);
          }
        }
        for (let i = 0; i < this.n(5); i++) {
          const f = rnd(0.4, 1.6);
          P.spawn(x + dx * f, y + dy * f, z + dz * f, dx * rnd(0.5, 1.5), rnd(0.2, 0.6), dz * rnd(0.5, 1.5), rnd(1.4, 2.2), 0.02, -0.2, 1.6, -1e5,
            PShape.Puff, PFlag.Soft, 0.2, rnd(0.8, 1.2), rnd(-0.5, 0.5), C.smokeLight, 0.6, C.smokeLight, 0.35, 1, 0.95);
        }
        break;
      }
      case W.Gentlemen: case W.Scoundrel: {
        // a fat cone of fire and a black-powder cloud that rolls forward
        const big = wid === W.Scoundrel ? 1.2 : 1;
        Cd.spawn(CMode.Muzzle, COrient.Axis, x, y, z, dx, dy, dz, 0.05, 0, 1.0 * big, 0.42 * big, C.fireCore, 1, C.fire, CFlag.Add, 0, Math.random(), 0, 0, 1.7 * k);
        Cd.spawn(CMode.Flash, COrient.Billboard, x + dx * 0.4, y + dy * 0.4, z + dz * 0.4, 0, 0, 0, 0.05, 0, 0.3 * big, 0.55 * big, C.white, 1, C.fire, CFlag.Add, 7, Math.random(), 0, Math.random() * 6, 2 * k);
        for (let i = 0; i < this.n(10); i++) {
          this.dir(0.1);
          const sp = rnd(5, 13);
          P.spawn(x, y, z, (dx + this.dx * 0.35) * sp, (dy + this.dy * 0.35) * sp, (dz + this.dz * 0.35) * sp, rnd(0.15, 0.3), 0, 6, 2.2, -1e5,
            PShape.Spark, PFlag.Add | PFlag.Stretch, 0.07, 0.03, 0, C.fireCore, 1, C.fire, 0.6, 2.4, 1.6);
        }
        for (let i = 0; i < this.n(7); i++) {
          this.dir(0.2);
          const sp = rnd(2.5, 6.5);
          P.spawn(x + dx * 0.4, y + dy * 0.4, z + dz * 0.4, (dx + this.dx * 0.5) * sp, (dy + this.dy * 0.5) * sp + 0.3, (dz + this.dz * 0.5) * sp, rnd(1.0, 1.7), 0.02, -0.25, 3.2, -1e5,
            PShape.Puff, PFlag.Soft, 0.2 * big, rnd(0.7, 1.1) * big, rnd(-1, 1), C.smokeLight, 0.85, C.smokeMid, 0.5, 1, 0.95);
        }
        break;
      }
      case W.BigIron: {
        // the hand-cannon bark: a fat star and a smoke ring that drifts off the muzzle
        Cd.spawn(CMode.Flash, COrient.Billboard, x + dx * 0.25, y + dy * 0.25, z + dz * 0.25, 0, 0, 0, 0.07, 0, 0.4, 0.75, C.white, 1, C.amber, CFlag.Add, 6, Math.random(), 0, Math.random() * 6, 2.6 * k);
        Cd.spawn(CMode.Shock, COrient.Plane, x + dx * 0.35, y + dy * 0.35, z + dz * 0.35, dx, dy, dz, 0.7, 0.03, 0.06, 0.32, C.smokeLight, 0.45, C.smokeLight, 0, 0.18, 0, 0, 0, 1);
        for (let i = 0; i < this.n(4); i++) {
          const f = rnd(0.2, 0.8);
          P.spawn(x + dx * f, y + dy * f, z + dz * f, dx * rnd(1, 2.5), rnd(0.2, 0.5), dz * rnd(1, 2.5), rnd(0.9, 1.4), 0.01, -0.2, 2.5, -1e5,
            PShape.Puff, PFlag.Soft, 0.12, rnd(0.45, 0.7), rnd(-1, 1), C.smokeLight, 0.75, C.smokeLight, 0.4, 1, 0.95);
        }
        break;
      }
      case W.AutoMG: case W.Starter: case W.Akimbo: {
        // a little heat haze of smoke on the hosing guns, building up on long strings
        if (Math.random() < 0.5 * this.mul) {
          P.spawn(x + dx * 0.2, y + dy * 0.2 + 0.05, z + dz * 0.2, dx * 0.6, rnd(0.5, 0.9), dz * 0.6, rnd(0.6, 1), 0, -0.3, 2, -1e5,
            PShape.Puff, PFlag.Soft, 0.06, rnd(0.25, 0.4), rnd(-1, 1), C.smokeLight, 0.55, C.smokeLight, 0.25, 1, 0.95);
        }
        break;
      }
      case W.DuelBlaster: {
        Cd.spawn(CMode.Shock, COrient.Plane, x + dx * 0.2, y + dy * 0.2, z + dz * 0.2, dx, dy, dz, 0.1, 0, 0.04, 0.3, C.violet, 1, C.white, CFlag.Add, 0.1, 0, 0, 0, 2.2 * k);
        for (let i = 0; i < this.n(6); i++) {
          this.dir(0);
          const sp = rnd(1.5, 4);
          P.spawn(x, y, z, this.dx * sp + dx * 2, this.dy * sp + dy * 2, this.dz * sp + dz * 2, rnd(0.2, 0.4), 0, 0, 3, -1e5,
            PShape.Diamond, PFlag.Add, 0.06, 0.02, 0, C.violet, 1, C.white, 0.4, 2.2, 1.6);
        }
        break;
      }
    }
  }

  // ---------------------------------------------------------------- tracer extras
  /** sniper rounds leave a smoke line that hangs in the air; MG every 4th round is a bright tracer */
  tracer(wid: number, fx_: number, fy: number, fz: number, tx: number, ty: number, tz: number, local: boolean, idx: number) {
    const dx = tx - fx_, dy = ty - fy, dz = tz - fz;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d < 1) return;
    if (wid === W.BoltSniper || (wid === W.BigIron && local)) {
      const sniper = wid === W.BoltSniper;
      const step = sniper ? 1.1 : 2.4;
      const n = Math.min(sniper ? 60 : 16, Math.floor(d / step * this.mul));
      for (let i = 1; i < n; i++) {
        const f = (i + Math.random() * 0.5) / n;
        this.dir(0);
        P_trail(this.fx, fx_ + dx * f, fy + dy * f, fz + dz * f, this.dx * 0.08, this.dy * 0.08 + 0.06, this.dz * 0.08, sniper ? rnd(1.2, 2.0) : rnd(0.6, 0.9), f * d / 900, sniper ? 0.07 : 0.05, sniper ? 0.32 : 0.18, sniper ? 0.42 : 0.28);
      }
      if (sniper) {
        // the hot core: a second, thicker streak that burns out slower than the shared tracer
        this.fx.cards.spawn(CMode.Streak, COrient.Segment, fx_ + dx * 0.02, fy + dy * 0.02, fz + dz * 0.02, tx, ty, tz,
          Math.max(d / 520 / 0.72, 0.14), 0, 0.16, 0.16, C.white, 1, C.amber, CFlag.Add, 1, 0, 0, 0, 3.2);
      }
    } else if (wid === W.AutoMG && idx % 4 === 0) {
      this.fx.cards.spawn(CMode.Streak, COrient.Segment, fx_ + dx * 0.03, fy + dy * 0.03, fz + dz * 0.03, tx, ty, tz,
        Math.max(d / 260 / 0.72, 0.16), 0, 0.12, 0.12, C.fireCore, 1, C.fire, CFlag.Add, Math.min(5 / d, 1), 0, 0, 0, 3.4);
    }
  }

  // ---------------------------------------------------------------- casings
  casing(shell: Shell, x: number, y: number, z: number, dx: number, dz: number) {
    if (shell === Shell.None) return;
    const D = this.fx.debris;
    const rx = -dz, rz = dx; // ejection port on the right
    const fl = this.ground(x, y, z);
    const s = shell === Shell.Rifle ? 1.25 : shell === Shell.Shotgun ? 1.8 : 1;
    const col = shell === Shell.Shotgun ? C.hullRed : C.brass;
    D.spawn(x - dx * 0.15, y + 0.05, z - dz * 0.15, rx * rnd(2.2, 3.4) - dx * 0.6, rnd(2.4, 3.6), rz * rnd(2.2, 3.4) - dz * 0.6,
      rnd(1.6, 2.4), 0, 0.022 * s, 0.022 * s, 0.06 * s, fl, rnd(20, 34), col, 16, 0.4, 1.6);
  }
  /** shotgun hulls come out on the reload, not on the shot */
  shellsOut(n: number, x: number, y: number, z: number, dx: number, dz: number) {
    for (let i = 0; i < n; i++) this.casing(Shell.Shotgun, x, y, z, dx * 0.3, dz * 0.3);
  }

  // ---------------------------------------------------------------- launches
  /** a rocket (k 1) or harpoon (k 0.5) leaves the tube: a forward flash, a backblast cone of smoke behind
   *  the shooter, and a dust skirt if the tube is near the ground */
  launch(k: number, x: number, y: number, z: number, dx: number, dy: number, dz: number, local: boolean) {
    const P = this.fx.particles, Cd = this.fx.cards, g = local ? 1 : 0.8;
    Cd.spawn(CMode.Muzzle, COrient.Axis, x, y, z, dx, dy, dz, 0.06, 0, 1.1 * k, 0.5 * k, C.fireCore, 1, C.fire, CFlag.Add, 0, Math.random(), 0, 0, 2.2 * g);
    Cd.spawn(CMode.Flash, COrient.Billboard, x + dx * 0.3, y + dy * 0.3, z + dz * 0.3, 0, 0, 0, 0.05, 0, 0.35 * k, 0.7 * k, C.white, 1, C.amber, CFlag.Add, 6, Math.random(), 0, Math.random() * 6, 2.4 * g);
    // backblast: hot puffs out of the back of the tube that cool to smoke
    for (let i = 0; i < this.n(7 * k); i++) {
      this.dir(0.35);
      const sp = rnd(4, 9) * k;
      P.spawn(x - dx * 0.5, y - dy * 0.5, z - dz * 0.5, (-dx + this.dx * 0.45) * sp, (-dy + this.dy * 0.45) * sp + 0.4, (-dz + this.dz * 0.45) * sp, rnd(0.7, 1.2), 0.03, -0.3, 3.4, -1e5,
        PShape.Puff, PFlag.Soft, 0.15 * k, rnd(0.6, 0.95) * k, rnd(-1, 1), i < 2 ? C.fireCore : C.smokeLight, 0.9, C.smokeMid, 0.45, 1, 0.94);
    }
    // forward smoke that the rocket punches through
    for (let i = 0; i < this.n(4 * k); i++) {
      const f = rnd(0.4, 1.6);
      P.spawn(x + dx * f, y + dy * f, z + dz * f, dx * rnd(1, 3) + rnd(-0.4, 0.4), rnd(0.2, 0.6), dz * rnd(1, 3) + rnd(-0.4, 0.4), rnd(0.8, 1.3), 0.02, -0.2, 3, -1e5,
        PShape.Puff, PFlag.Soft, 0.12 * k, rnd(0.45, 0.7) * k, rnd(-1, 1), C.smokeLight, 0.7, C.smokeLight, 0.3, 1, 0.95);
    }
    const fl = this.ground(x, y, z);
    if (y - fl < 2.2 && k >= 1) {
      // the backblast kicks dust off the ground behind you
      for (let i = 0; i < this.n(5); i++) {
        const a = Math.atan2(-dx, -dz) + rnd(-0.9, 0.9), sp = rnd(2.5, 5);
        P.spawn(x - dx, fl + 0.15, z - dz, Math.sin(a) * sp, rnd(0.3, 0.9), Math.cos(a) * sp, rnd(0.6, 1), 0.04, -0.1, 3, fl,
          PShape.Puff, PFlag.Soft, 0.2, rnd(0.5, 0.8), rnd(-1, 1), C.smokeLight, 0.6, C.smokeMid, 0.3, 1, 0.92);
      }
    }
  }

  // ---------------------------------------------------------------- impacts
  /** directional spray: sparks leave along the bullet's reflection, harder for heavy rounds */
  impact(kind: number, x: number, y: number, z: number, sx: number, sy: number, sz: number, nx: number, ny: number, nz: number, heavy: number) {
    const P = this.fx.particles;
    if (kind === 0) return;
    // reflect the shot about the surface normal
    const dn = sx * nx + sy * ny + sz * nz;
    const rx = sx - 2 * dn * nx, ry = sy - 2 * dn * ny, rz = sz - 2 * dn * nz;
    const metal = kind >= 2;
    const n = this.n((metal ? 6 : 3) * (0.6 + heavy));
    for (let i = 0; i < n; i++) {
      this.dir(0.2);
      const sp = rnd(6, 14) * (0.7 + 0.5 * heavy);
      P.spawn(x, y, z, (rx * 0.8 + this.dx * 0.5) * sp, (ry * 0.8 + this.dy * 0.5) * sp + 1, (rz * 0.8 + this.dz * 0.5) * sp, rnd(0.15, 0.32), 0, 14, 1.5, -1e5,
        PShape.Spark, PFlag.Add | PFlag.Stretch, 0.09, 0.04, 0, kind === 4 ? C.white : C.fireCore, 1, kind === 4 ? C.steel : C.fire, 0.6, 2.4, 1.4);
    }
    if (kind === 4) {
      // armour: the round glances. A small hollow steel ring at the hit, no warm glow (it isn't hurting)
      this.fx.cards.spawn(CMode.Shock, COrient.Billboard, x - sx * 0.1, y - sy * 0.1, z - sz * 0.1, 0, 0, 0, 0.16, 0, 0.06, 0.42, C.chrome, 0.9, C.steel, CFlag.Add, 0.1, 0, 0, 0, 1.6);
    }
    if (metal && kind !== 5 && heavy > 0.4) {
      // a hot fleck of casing knocked off: tumbles and lands
      const fl = this.ground(x, y, z);
      for (let i = 0; i < this.n(heavy > 1 ? 3 : 1); i++) {
        this.dir(0.5);
        this.fx.debris.spawn(x, y, z, (nx + this.dx * 0.6) * rnd(2, 4), rnd(2, 4), (nz + this.dz * 0.6) * rnd(2, 4), rnd(1.2, 1.8), 0,
          rnd(0.05, 0.09), 0.012, rnd(0.04, 0.07), fl, rnd(14, 26), Math.random() < 0.5 ? C.gunmetal : C.chrome, 18, 0.5, 1);
      }
    }
    if (heavy > 1.2 && kind >= 2) {
      // big rounds punch a puff of shredded paint out of the far side
      for (let i = 0; i < this.n(3); i++) {
        this.dir(0.2);
        P.spawn(x, y, z, sx * rnd(2, 5) + this.dx, sy * rnd(2, 5) + this.dy + 0.4, sz * rnd(2, 5) + this.dz, rnd(0.5, 0.9), 0, -0.2, 3.5, -1e5,
          PShape.Puff, PFlag.Soft, 0.12, rnd(0.4, 0.6), rnd(-1, 1), C.smokeLight, 0.9, C.smokeMid, 0.6, 1, 0.95);
      }
    }
  }

  // ---------------------------------------------------------------- deaths
  /** extra FX for a kill, on top of fx.robotDeath. pos = the robot's feet. */
  kill(kind: DeathKind, type: number, x: number, y: number, z: number, dx: number, dz: number, pow: number) {
    const def = ENEMIES[type] ?? ENEMIES[0];
    const inks = INKS[type] ?? INKS[0];
    const P = this.fx.particles, D = this.fx.debris, Cd = this.fx.cards;
    const big = type === 5 ? 1.6 : 1;
    const fl = this.ground(x, y + 0.5, z);
    const cy = y + def.bodyY;
    if (kind === DeathKind.Headshot) {
      const hy = y + def.headY;
      // the top part goes up in a spinning arc, a copy in the robot's own colours
      const s = def.headR * 1.5 * big;
      D.spawn(x, hy, z, dx * rnd(1.5, 2.5) + rnd(-0.8, 0.8), rnd(7.5, 9.5), dz * rnd(1.5, 2.5) + rnd(-0.8, 0.8), 3, 0.07, s, s * 0.55, s * 0.9, fl, rnd(18, 26), inks[0], 18, 0.3);
      // a gold pop at the neck, then a fountain of sparks out of it for a beat
      Cd.spawn(CMode.Flash, COrient.Billboard, x, hy, z, 0, 0, 0, 0.12, 0.06, 0.4, 1.0, C.white, 1, C.amber, CFlag.Add, 8, Math.random(), 0, Math.random() * 6, 3.4);
      Cd.spawn(CMode.Shock, COrient.Billboard, x, hy, z, 0, 0, 0, 0.3, 0.06, 0.1, 1.4, C.amber, 1, C.brassHi, CFlag.Add, 0.12, 0, 0, 0, 2.2);
      this.emit(Em.Fountain, x, hy - 0.1, z, 0.9, big);
    }
    if (kind === DeathKind.Shatter || kind === DeathKind.Blown) {
      // the casing comes apart: big inked panels and a spray of nuts and springs
      const np = this.n((kind === DeathKind.Shatter ? 10 : 5) + def.radius * 4);
      for (let i = 0; i < np; i++) {
        this.dir(0.5);
        const w = def.radius * rnd(0.45, 0.9) * (type === 5 ? 1.3 : 1);
        const sp = rnd(3, 8) * (0.8 + 0.4 * Math.min(2, pow));
        D.spawn(x + this.dx * def.radius * 0.5, cy + this.dy * def.radius * 0.4, z + this.dz * def.radius * 0.5,
          this.dx * sp + dx * 3 * pow, this.dy * sp * 0.6 + rnd(3, 7), this.dz * sp + dz * 3 * pow,
          rnd(2.4, 3.2), 0.02, w, w * rnd(0.1, 0.18), w * rnd(0.5, 0.9), fl, rnd(10, 22), inks[i % 3]);
      }
      for (let i = 0; i < this.n(8); i++) {
        this.dir(0.6);
        const sp = rnd(5, 11);
        P.spawn(x, cy, z, this.dx * sp, this.dy * sp + 2, this.dz * sp, rnd(1, 1.6), 0, 16, 0.5, fl,
          PShape.Nut, 0, 0.07, 0.07, rnd(-20, 20), C.gunmetal, 1, C.chrome, 1, 1, 1);
      }
      Cd.spawn(CMode.Flash, COrient.Billboard, x, cy, z, 0, 0, 0, 0.14, 0.05, def.radius * 1.0, def.radius * 2.4, C.white, 1, C.fire, CFlag.Add, 8, Math.random(), 0, Math.random() * 6, 3.6);
    }
    if (kind === DeathKind.Topple) {
      // even the plain kills pop: a hot flash, a few panels and a spit of sparks at the instant of death
      Cd.spawn(CMode.Flash, COrient.Billboard, x, cy, z, 0, 0, 0, 0.09, 0, def.radius * 0.6, def.radius * 1.6, C.white, 1, C.fire, CFlag.Add, 7, Math.random(), 0, Math.random() * 6, 3);
      for (let i = 0; i < this.n(3 + def.radius * 2); i++) {
        this.dir(0.5);
        const w = def.radius * rnd(0.3, 0.55) * big, sp = rnd(2.5, 5);
        D.spawn(x + this.dx * def.radius * 0.4, cy, z + this.dz * def.radius * 0.4, this.dx * sp + dx * 1.5, rnd(3, 6), this.dz * sp + dz * 1.5,
          rnd(2, 2.8), 0.02, w, w * rnd(0.1, 0.18), w * rnd(0.5, 0.9), fl, rnd(10, 20), inks[i % 3]);
      }
      for (let i = 0; i < this.n(8); i++) {
        this.dir(0.3);
        const sp = rnd(5, 10);
        P.spawn(x, cy, z, this.dx * sp, this.dy * sp + 2, this.dz * sp, rnd(0.2, 0.4), 0, 14, 1.5, -1e5,
          PShape.Spark, PFlag.Add | PFlag.Stretch, 0.08, 0.04, 0, C.fireCore, 1, C.fire, 0.6, 2.4, 1.4);
      }
    }
    // every wreck keeps going for a few seconds: blown / shattered ones burn, the rest smoke
    this.emit(kind === DeathKind.Topple || kind === DeathKind.Headshot ? Em.Smoke : Em.Burn, x, cy, z, 2.6, big * def.radius);
  }

  private emit(k: Em, x: number, y: number, z: number, secs: number, s: number) {
    let i = 0;
    for (; i < MAX_EM; i++) if (this.eK[i] < 0 || this.eEnd[i] < this.time) break;
    if (i === MAX_EM) i = this.eN++ % MAX_EM;
    this.eK[i] = k; this.eX[i] = x; this.eY[i] = y; this.eZ[i] = z; this.eEnd[i] = this.time + secs; this.eAcc[i] = 0; this.eS[i] = s;
  }

  // ---------------------------------------------------------------- damage states
  /** a hurt robot shows it: under half hull it smokes, under a quarter it spits sparks and burns */
  damaged(id: number, type: number, x: number, y: number, z: number, hp: number) {
    if (hp > 0.5 || hp <= 0) return;
    const t = this.time;
    const nx = this.nextPuff.get(id) ?? 0;
    if (t < nx) return;
    const def = ENEMIES[type] ?? ENEMIES[0];
    const crit = hp < 0.25;
    this.nextPuff.set(id, t + (crit ? 0.11 : 0.3) / this.mul * rnd(0.7, 1.3));
    if (this.nextPuff.size > 200) this.nextPuff.clear();
    const P = this.fx.particles;
    const top = y + def.headY - def.headR * 0.3;
    const ox = rnd(-0.5, 0.5) * def.radius, oz = rnd(-0.5, 0.5) * def.radius;
    P.spawn(x + ox, top, z + oz, rnd(-0.2, 0.2), rnd(0.9, 1.5), rnd(-0.2, 0.2), rnd(1.0, 1.6), 0, -0.3, 1.2, -1e5,
      PShape.Puff, PFlag.Soft, 0.1 * (crit ? 1.4 : 1), rnd(0.45, 0.75) * (crit ? 1.4 : 1), rnd(-1, 1), crit ? C.smokeDark : C.smokeMid, 0.8, C.smokeLight, 0.35, 1, 0.92);
    if (crit && Math.random() < 0.6) {
      for (let i = 0; i < this.n(4); i++) {
        this.dir(0.5);
        const sp = rnd(2, 5);
        P.spawn(x + ox, y + def.bodyY + rnd(0, 0.4), z + oz, this.dx * sp, this.dy * sp + 1.5, this.dz * sp, rnd(0.2, 0.45), 0, 12, 1.2, -1e5,
          PShape.Spark, PFlag.Add | PFlag.Stretch, 0.06, 0.03, 0, C.fireCore, 1, C.fire, 0.5, 2.2, 1.5);
      }
      if (Math.random() < 0.35) {
        P.spawn(x + ox, top - 0.1, z + oz, 0, rnd(0.8, 1.4), 0, rnd(0.25, 0.4), 0, -0.5, 2, -1e5,
          PShape.Fire, PFlag.Add | PFlag.Up, 0.15, 0.22, 0, linRGB(0xffb83a), 1, linRGB(0xe8461c), 0.9, 1.5, 1.5);
      }
    }
  }

  // ---------------------------------------------------------------- frame
  update(dt: number, time: number) {
    this.time = time;
    const P = this.fx.particles;
    for (let i = 0; i < MAX_EM; i++) {
      const k = this.eK[i];
      if (k < 0) continue;
      if (time > this.eEnd[i]) { this.eK[i] = -1; continue; }
      const left = this.eEnd[i] - time;
      const s = this.eS[i];
      const rate = k === Em.Fountain ? 70 : k === Em.Burn ? 14 : 7;
      this.eAcc[i] += dt * rate * this.mul;
      const x = this.eX[i], y = this.eY[i], z = this.eZ[i];
      while (this.eAcc[i] >= 1) {
        this.eAcc[i] -= 1;
        if (k === Em.Fountain) {
          this.dir(0.85);
          const sp = rnd(3, 7) * Math.min(1, left * 1.6 + 0.2);
          P.spawn(x, y, z, this.dx * sp * 0.5, Math.abs(this.dy) * sp + 1.5, this.dz * sp * 0.5, rnd(0.35, 0.65), 0, 14, 1, -1e5,
            PShape.Spark, PFlag.Add | PFlag.Stretch, 0.07, 0.03, 0, C.fireCore, 1, C.amber, 0.5, 2.4, 1.6);
        } else if (k === Em.Burn) {
          const r = 0.4 * s;
          P.spawn(x + rnd(-r, r), y - 0.3 * s, z + rnd(-r, r), rnd(-0.2, 0.2), rnd(1, 1.8), rnd(-0.2, 0.2), rnd(0.3, 0.5), 0, -0.5, 2, -1e5,
            PShape.Fire, PFlag.Add | PFlag.Up, 0.25 * s, 0.4 * s, 0, linRGB(0xffb83a), 1, linRGB(0xe8461c), 0.9, 1.5, 1.5);
          if (Math.random() < 0.5) P.spawn(x + rnd(-r, r), y, z + rnd(-r, r), rnd(-0.2, 0.2), rnd(1.2, 2), rnd(-0.2, 0.2), rnd(1.4, 2.2), 0, -0.3, 1, -1e5,
            PShape.Puff, PFlag.Soft, 0.25 * s, rnd(0.9, 1.4) * s, rnd(-1, 1), C.smokeDark, 0.85, C.smokeMid, 0.3, 1, 0.92);
        } else {
          const r = 0.3 * s;
          P.spawn(x + rnd(-r, r), y - 0.2 * s, z + rnd(-r, r), rnd(-0.15, 0.15), rnd(0.8, 1.3), rnd(-0.15, 0.15), rnd(1.4, 2.2), 0, -0.3, 1, -1e5,
            PShape.Puff, PFlag.Soft, 0.18 * s, rnd(0.7, 1.1) * s, rnd(-1, 1), C.smokeMid, 0.7, C.smokeLight, 0.25, 1, 0.92);
          if (Math.random() < 0.25) {
            this.dir(0.6);
            P.spawn(x, y - 0.1 * s, z, this.dx * 3, this.dy * 3 + 1, this.dz * 3, rnd(0.2, 0.35), 0, 12, 1.2, -1e5,
              PShape.Spark, PFlag.Add | PFlag.Stretch, 0.05, 0.025, 0, C.fireCore, 1, C.fire, 0.5, 2.2, 1.5);
          }
        }
      }
    }
    void this.v;
  }
}

function P_trail(fx: Fx, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, delay: number, s0: number, s1: number, a: number) {
  fx.particles.spawn(x, y, z, vx, vy, vz, life, delay, -0.05, 0.8, -1e5, PShape.Puff, PFlag.Soft, s0, s1, rnd(-0.6, 0.6), C.smokeLight, a, C.smokeLight, 0, 1, 1);
}
